/**
 * ehr#488: the repeat rule's date math, shared so the server can roll a repeating task forward when
 * it's completed (the web's RecurrenceField re-exports these; they were defined there, #4 / #22).
 * Date-only, local: 'YYYY-MM-DD' in, 'YYYY-MM-DD' out.
 */
function parseISO(v) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    if (!m)
        return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
}
function toISO(d) {
    const p = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** Which occurrence of this weekday in the month (0-based) + whether it is the last. */
export function ordinalOfMonth(d) {
    const day = d.getDate();
    const nth = Math.floor((day - 1) / 7); // 0..4
    const isLast = day + 7 > new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return { nth, isLast };
}
/** The next occurrence strictly after `fromISO`, per the rule. Returns YYYY-MM-DD. */
export function nextOccurrence(fromISO, rec) {
    const start = parseISO(fromISO);
    if (!start)
        return fromISO;
    const n = rec.interval || 1;
    if (rec.freq === 'weekly' && (rec.weekdays ?? []).length) {
        const days = [...new Set(rec.weekdays)].sort((a, b) => a - b);
        const startDow = start.getDay();
        // Next selected weekday still ahead in the (on-cycle) current week.
        const laterThisWeek = days.find((dow) => dow > startDow);
        if (laterThisWeek != null) {
            const cand = new Date(start);
            cand.setDate(cand.getDate() + (laterThisWeek - startDow));
            return toISO(cand);
        }
        // Otherwise the first selected weekday of the next on-cycle week.
        const weekStart = new Date(start);
        weekStart.setDate(weekStart.getDate() - startDow + 7 * n);
        weekStart.setDate(weekStart.getDate() + days[0]);
        return toISO(weekStart);
    }
    const d = new Date(start);
    if (rec.freq === 'daily')
        d.setDate(d.getDate() + n);
    else if (rec.freq === 'weekly')
        d.setDate(d.getDate() + 7 * n);
    else if (rec.freq === 'monthly') {
        if (rec.monthlyMode === 'weekday') {
            const { nth, isLast } = ordinalOfMonth(d);
            const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
            const want = d.getDay();
            const daysInMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
            const matches = [];
            for (let day = 1; day <= daysInMonth; day++) {
                const c = new Date(target.getFullYear(), target.getMonth(), day);
                if (c.getDay() === want)
                    matches.push(c);
            }
            const last = matches[matches.length - 1];
            const pick = isLast ? last : (matches[nth] ?? last);
            return toISO(pick);
        }
        d.setMonth(d.getMonth() + n);
    }
    else if (rec.freq === 'yearly')
        d.setFullYear(d.getFullYear() + n);
    return toISO(d);
}
/** Has the series ended (after advancing to `nextISO`)? */
export function recurrenceEnded(rec, nextISO) {
    if (!rec || !rec.ends || rec.ends.type === 'never')
        return false;
    if (rec.ends.type === 'after')
        return (rec.countDone || 0) + 1 >= (rec.ends.count || Infinity);
    if (rec.ends.type === 'on' && rec.ends.date) {
        const next = parseISO(nextISO);
        const end = parseISO(rec.ends.date);
        return !!next && !!end && next > end;
    }
    return false;
}
//# sourceMappingURL=recurrence.js.map